import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { Search, RefreshCw } from 'lucide-react';
import { useMobile } from '../hooks/useMobile';

const RAG_DOT = { red: '#ef4444', amber: '#f59e0b', green: '#10b981' };

export default function ClientsPage() {
  const mobile = useMobile();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = async (q) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = q ? `/api/broker/clients?search=${encodeURIComponent(q)}` : '/api/broker/clients';
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setClients(Array.isArray(data.clients) ? data.clients : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  return (
    // .broker-dashboard / .main-content (not a bespoke inline flex wrapper) — the proven
    // sidebar-offset pattern every other page uses (see App.css's comment: the fixed 70px
    // sidebar is position:fixed and takes no space in a plain flex layout, so without this
    // shared class the sidebar overlaps the first 70px of content). This page originally used
    // its own inline-style flex wrapper and never got the offset — fixed 6 August 2026.
    <div className="broker-dashboard">
      <Sidebar />
      <main className="main-content" style={{ maxWidth: 1100, ...(mobile ? {} : { padding: '28px 32px' }) }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#0e1b2c' }}>Clients</h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#718096' }}>Every client across your deals — primary applicants and any additional co-applicants.</p>
          </div>
          <button onClick={() => load(search)} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', cursor: 'pointer' }}>
            <RefreshCw size={15} />
          </button>
        </div>

        <div style={{ position: 'relative', marginBottom: 16, maxWidth: 360 }}>
          <Search size={14} style={{ color: '#9ca3af', position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px 9px 32px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, fontFamily: 'inherit' }}
            placeholder="Search by name, email, phone or deal reference..."
            value={search} onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '2fr 1.4fr 1fr 1fr 1fr', gap: 8, padding: '10px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8' }}>
            <span>Client</span>
            {!mobile && <span>Contact</span>}
            {!mobile && <span>Deal</span>}
            {!mobile && <span>Stage</span>}
            {!mobile && <span>Portal</span>}
          </div>

          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#a0aec0', fontSize: 13 }}>Loading...</div>
          ) : clients.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#a0aec0', fontSize: 13 }}>
              {search ? 'No clients match your search.' : 'No clients yet — they\'ll appear here once a deal is created.'}
            </div>
          ) : (
            clients.map(c => (
              <div key={`${c.deal_id}-${c.additional_client_id || 'primary'}`}
                onClick={() => navigate(`/deal/${c.deal_id}`)}
                style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '2fr 1.4fr 1fr 1fr 1fr', gap: 8, padding: '12px 16px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13.5, color: '#0e1b2c' }}>{c.name}</div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: c.is_primary ? '#065f46' : '#4f46e5', background: c.is_primary ? '#d1fae5' : '#eef2ff', padding: '2px 8px', borderRadius: 10, display: 'inline-block', marginTop: 3 }}>
                    {c.is_primary ? 'Primary' : c.relationship}
                  </span>
                </div>
                <div style={{ fontSize: 12.5, color: '#4a5568' }}>
                  {c.email && <div>{c.email}</div>}
                  {c.phone && <div>{c.phone}</div>}
                </div>
                <div style={{ fontSize: 12.5, color: '#1a56db', fontWeight: 600 }}>{c.reference || '—'}</div>
                <div>
                  {c.stage_name ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#4a5568' }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: RAG_DOT[c.stage_colour] || '#94a3b8', display: 'inline-block' }} />
                      {c.stage_name}
                    </span>
                  ) : <span style={{ color: '#cbd5e0', fontSize: 12 }}>—</span>}
                </div>
                <div>
                  {c.has_portal_login ? (
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#065f46', background: '#d1fae5', padding: '3px 8px', borderRadius: 10 }}>✓ Active</span>
                  ) : (
                    <span style={{ fontSize: 11, color: '#a0aec0' }}>No login</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
